import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../../shared';
import { ITrajectoryDetail } from '../../../../config/api/models/Trajectory';
import { EditViewModeButton } from '../../../shared/components';
import { EditViewModeWrapper } from './Atoms';
import ReactHtmlParser from 'react-html-parser';
import { useAppDispatch, useAppSelector } from '../../../shared/hooks/useAppDispatch';
import {
  lockTrajectory,
  unlockTrajectory,
} from '../../../shared/slices/trajectories/api/trajectoryApiSlice';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import {
  setActiveFilter,
  setSelectedGroup,
  setSelectedStudent,
} from 'src/modules/shared/slices/filters/entity/FiltersSlice';

export type TrajectoryViewModeContentProps = {
  trajectory: ITrajectoryDetail;
};

export const TrajectoryViewModeContent: FC<TrajectoryViewModeContentProps> = ({ trajectory }) => {
  const { t } = useTranslation();
  const { isAuthor, isStudent, isAdmin, isSuperAdmin, user } = useUser();
  const dispatch = useAppDispatch();
  const viewMode = useAppSelector((state) => state.trajectories.viewMode);
  const lockStatus = useAppSelector((state) => state.trajectories.lockStatus);

  const canEditTrajectory = useMemo(() => {
    return (trajectory.isOwner || trajectory.isCollab || isAuthor) && !isStudent;
  }, [trajectory, isAuthor, isStudent]);

  const editBtnDisabled = useMemo(() => {
    let result = false;

    if (!canEditTrajectory) result = true;
    if (lockStatus != null && lockStatus.locked && lockStatus.lockedBy?.id !== user.id)
      result = true;
    if (trajectory.isFrozen && !(isAdmin || isSuperAdmin)) result = true;

    return result;
  }, [lockStatus, canEditTrajectory, trajectory, user, isAdmin, isSuperAdmin]);

  const handleToggleViewMode = useCallback(() => {
    if (viewMode === 'read') {
      // switch to edit mode
      dispatch(lockTrajectory(trajectory.id));
      dispatch(setActiveFilter(undefined));
      dispatch(setSelectedGroup(''));
      dispatch(setSelectedStudent(''));
    } else {
      // switch to read mode
      dispatch(unlockTrajectory(trajectory.id));
    }
  }, [viewMode, dispatch, trajectory.id]);

  return canEditTrajectory ? (
    <EditViewModeWrapper>
      {viewMode === 'read' ? (
        <EditViewModeButton
          disabled={editBtnDisabled}
          icon={SpriteIconsLight.EDIT}
          onClick={lockStatus == null ? undefined : handleToggleViewMode}
          tooltip={
            trajectory.isFrozen
              ? t('differentiations.frozenTooltip')
              : lockStatus?.lockedBy != null
              ? ReactHtmlParser(
                  t('differentiations.lockedTooltip').replace(
                    '%NAME%',
                    `${lockStatus.lockedBy.firstName} ${lockStatus.lockedBy.name}`
                  )
                )
              : undefined
          }
        >
          {t('differentiations.editContent')}
        </EditViewModeButton>
      ) : (
        <EditViewModeButton icon={SpriteIconsLight.CANCEL} onClick={handleToggleViewMode}>
          {t('differentiations.cancelEditMode')}
        </EditViewModeButton>
      )}
    </EditViewModeWrapper>
  ) : null;
};
